import React, { type ReactNode } from "react";
import { useUiStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreUiComponents,
  type InputErrorType,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import clsx from "clsx";
import defaultStyles from "./InputContainer.module.scss";

export type InputContainerProps = {
  id: string;
  label?: string;
  hasValue?: boolean;
  defaultValue?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  hasError?: boolean;
  startContent?: ReactNode;
  endContent?: ReactNode;
  disabled?: boolean;
} & PlatformComponentProps;

const COMPONENT_KEY = CoreUiComponents.InputContainer;

export const InputContainer: React.FC<InputContainerProps> = ({
  id,
  label,
  className,
  startContent,
  endContent,
  children,
  hasValue = false,
  hasError = false,
}) => {
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY, {
    error: hasError,
    filled: hasValue,
  });

  return (
    <div className={clsx(block, className)}>
      <div className={cn("content")}>
        {startContent != null ? (
          <div className={cn("start-content")}>{startContent}</div>
        ) : null}
        <div className={cn("input-root")}>
          {label != null ? (
            <label htmlFor={id} className={cn("label")}>
              {label}
            </label>
          ) : null}
          <div className={cn("input-content")}>{children}</div>
        </div>
        {endContent != null ? (
          <div className={cn("end-content")}>{endContent}</div>
        ) : null}
      </div>
    </div>
  );
};

export const InputError: React.FC<{
  error?: InputErrorType;
}> = ({ error }) => {
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [_block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  return error != null && error.message !== "" ? (
    <div className={cn("error")} role="alert">
      {error.message}
    </div>
  ) : null;
};
